<template>
  <div class="tools-container">
    <div class="content-area">
      <div class="page-header">
        <h1 class="page-title">
          Tools
        </h1>
        <v-btn
          v-if="activeTab === 'custom'"
          color="primary"
          class="mank-btn"
          @click="createTool"
          elevation="0">
          <Icon name="plus" variant="solid" color="onPrimary"/>
          Create Tool
        </v-btn>
      </div>

      <div class="main-content">
        <!-- Tabs Area -->
        <div class="tabs-area mank-tabs-strip">
          <div class="tabs-container">
            <v-btn
              :color="activeTab === 'custom' ? 'primary' : 'grey-lighten-2'"
              :variant="activeTab === 'custom' ? 'flat' : 'text'"
              size="small"
              elevation="0"
              @click="setActiveTab('custom')"
              class="tab-button"
              :class="{ 'active-tab': activeTab === 'custom' }">
              Custom
            </v-btn>

            <v-btn
              :color="activeTab === 'builtin' ? 'primary' : 'grey-lighten-2'"
              :variant="activeTab === 'builtin' ? 'flat' : 'text'"
              size="small"
              elevation="0"
              @click="setActiveTab('builtin')"
              class="tab-button"
              :class="{ 'active-tab': activeTab === 'builtin' }">
              Built-In
            </v-btn>
          </div>
        </div>

        <!-- Cards Area -->
        <div class="cards-area">
          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <v-progress-circular indeterminate color="primary" size="40"/>
            <p class="loading-text">
              Loading tools...
            </p>
          </div>

          <div v-else class="cards-container">
            <!-- Tool Cards -->
            <div
              v-for="tool in filteredTools"
              :key="tool.id"
              class="tool-card">
              <!-- Title Area -->
              <div class="title-area">
                <div class="title-section">
                  <h3 class="tool-name ds-list-heading">
                    {{ tool.name }}
                  </h3>
                  <div class="tool-actions">
                    <div class="tool-id-section">
                      <span class="tool-id">{{ tool.id }}</span>
                      <v-btn
                        v-if="activeTab === 'custom'"
                        icon
                        @click="copyToolId(tool.id)"
                        class="view-btn mank-btn-icon ">
                        <Icon name="copy" variant="solid" size="16" color="muted"/>
                      </v-btn>
                    </div>
                    <v-btn
                      v-if="activeTab === 'custom'"
                      icon
                      @click="viewTool(tool)"
                      class="view-btn mank-btn-icon ">
                      <Icon name="arrow-right" variant="solid" size="16" color="default"/>
                    </v-btn>
                    <v-btn
                      v-if="activeTab === 'custom'"
                      icon
                      @click="confirmDeleteTool(tool)"
                      class="delete-btn mank-btn-icon ">
                      <Icon name="trash" variant="solid" color="default" size="16"/>
                    </v-btn>
                  </div>
                </div>
              </div>

              <!-- Description Area -->
              <div class="description-area">
                <p class="tool-description">
                  {{ tool.description }}
                </p>
              </div>
            </div>

            <!-- Empty State -->
            <div v-if="filteredTools.length === 0" class="empty-state">
              <div class="empty-illustration">
                <div class="tool-icon-container">
                  <v-icon size="40" color="primary" class="tool-icon">
                    mdi-hammer-wrench
                  </v-icon>
                </div>
              </div>
              <div class="empty-text">
                <h3 class="empty-title ds-panel-title">
                  {{ activeTab === 'builtin' ? 'No Tools Found' : 'No Custom Tool Created' }}
                </h3>
                <p class="empty-description">
                  {{ activeTab === 'builtin' ? 'No built-in tools available.' : 'You can create your own tool by clicking the button below.' }}
                </p>
              </div>
              <div v-if="activeTab === 'custom'" class="empty-actions">
                <v-btn
                  color="primary"
                  @click="createTool"
                  elevation="0"
                  class="create-tool-btn">
                  <v-icon size="16" start>
                    mdi-plus
                  </v-icon>
                  Create Tool
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <DeleteConfirmationModal
      :visible="showDeleteDialog"
      @cancel="cancelDelete"
      @confirm="confirmDelete"/>
  </div>
</template>

<script>
import Icon from '@/components/Icon.vue';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue';
import ToolService from '@/services/ToolService';

export default {
  name: 'Tools',
  components: {
    Icon,
    DeleteConfirmationModal,
  },
  data() {
    return {
      searchQuery: '',
      activeTab: 'custom',
      loading: false,
      tools: [],
      showDeleteDialog: false,
      toolToDelete: null,
    };
  },
  computed: {
    filteredTools() {
      let tools = this.tools.filter((tool) => tool.type === this.activeTab);

      if (this.searchQuery) {
        tools = tools.filter((tool) => tool.name.toLowerCase().includes(this.searchQuery.toLowerCase())
          || tool.description.toLowerCase().includes(this.searchQuery.toLowerCase()));
      }

      return tools;
    },
    builtinTools() {
      return this.tools.filter((tool) => tool.type === 'builtin');
    },
    customTools() {
      return this.tools.filter((tool) => tool.type === 'custom');
    },
  },
  async mounted() {
    await this.fetchTools();
  },
  methods: {
    async fetchTools() {
      this.loading = true;
      try {
        const response = await ToolService.getAllTools();
        this.tools = response.data || [];

        // API'den gelen tool'lara type field'ı ekle
        // Şimdilik tüm tool'ları custom olarak işaretle
        // Built-in tool'lar için ayrı bir endpoint veya field gerekebilir
        this.tools = this.tools.map((tool) => ({
          ...tool,
          type: 'custom', // API'de type field'ı yoksa default olarak custom
        }));
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Failed to fetch tools',
        });
      } finally {
        this.loading = false;
      }
    },
    createTool() {
      this.$router.push({ name: 'CreateTool' });
    },
    setActiveTab(tab) {
      this.activeTab = tab;
    },
    async copyToolId(id) {
      try {
        await navigator.clipboard.writeText(id);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Copied',
          message: 'Tool ID copied to clipboard',
        });
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Failed to copy ID',
        });
      }
    },
    viewTool(tool) {
      this.$router.push({
        name: 'ToolDetail',
        params: { id: tool.id },
      });
    },
    confirmDeleteTool(tool) {
      this.toolToDelete = tool;
      this.showDeleteDialog = true;
    },
    cancelDelete() {
      this.showDeleteDialog = false;
      this.toolToDelete = null;
    },
    async confirmDelete() {
      if (this.toolToDelete) {
        try {
          await ToolService.deleteTool(this.toolToDelete.id);
          this.$store.dispatch('ui/showNotification', {
            type: 'success',
            title: 'Success',
            message: 'Tool deleted successfully',
          });
          await this.fetchTools(); // Listeyi yenile
        } catch (error) {
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            title: 'Error',
            message: 'Failed to delete tool',
          });
        }
      }
      this.showDeleteDialog = false;
      this.toolToDelete = null;
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';
@import '../styles/colors.scss';

.main-content {
  gap: 10px;
}

.cards-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;
  gap: 10px;

  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    gap: 16px;

    .loading-text {
      font-family: $body-font-family;
      font-size: $body-3-size;
      color: $light-foreground-3;
      margin: 0;
    }
  }

  .cards-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    align-self: stretch;
    gap: 12px;

    .tool-card {
      border-radius: 12px;
      border: 1px solid $light-border; // #D3DAE0
      background-color: $light-surface-2; // #FFFFFF - Figma'daki card background
      overflow: hidden;

      .title-area {
        padding: 8px 12px;
        gap: 10px;
        background-color: $light-surface-1; // #F7F8FB - Figma'daki title area background
        border-bottom: 1px solid $light-border; // #D3DAE0

        .title-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          align-self: stretch;
          gap: 24px;

          .tool-name {
            margin: 0;
          }

          .tool-actions {
            display: flex;
            align-items: center;
            gap: 12px;

            .tool-id-section {
              display: flex;
              align-items: center;
              gap: 4px;

              .tool-id {
                font-family: 'Plus Jakarta Sans', sans-serif;
                font-weight: 600;
                font-size: $body-3-size;
                line-height: 1.2;
                color: $light-foreground-1; // #171D22 - Figma'daki ID color
              }

              .copy-btn {
                width: 24px;
                height: 24px;
                padding: 4px;

                :deep(.v-icon) {
                  color: $light-foreground-1; // #171D22 - Figma'daki copy icon color
                }
              }
            }

            .view-btn {
              width: 24px;
              height: 24px;
              padding: 4px;
              background-color: $light-surface-2; // #FFFFFF

              :deep(.v-icon) {
                color: $light-foreground-1; // #171D22 - Figma'daki arrow icon color
              }
            }
          }
        }
      }

      .description-area {
        padding: 12px;
        gap: 24px;
        background-color: $light-surface-1; // #F7F8FB - Figma'daki description background

        .tool-description {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-weight: 400;
          font-size: $body-3-size;
          line-height: 1.6;
          color: $light-foreground-3; // #5C6A75 - Figma'daki description color
          margin: 0;
        }
      }
    }
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex: 1;
  padding: 48px 24px;

  .empty-illustration {
    .tool-icon-container {
      width: 80px;
      height: 80px;
      background-color: $light-surface-1; // #F7F8FB - Figma'daki circle background
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 16px;

      .tool-icon {
        color: $light-primary !important; // #EB575D - Figma'daki icon color
      }
    }
  }

  .empty-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    text-align: center;

    .empty-title {
      margin: 0;
    }

    .empty-description {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 400;
      font-size: $body-3-size;
      line-height: 1.6; // Figma'da 1.6 line-height
      color: $light-foreground-3; // #5C6A75 - Figma'daki description color
      margin: 0;
    }
  }

  .empty-actions {
    .create-tool-btn {
      height: 32px;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 600;
      font-size: $body-3-size;
      line-height: 1.2;
      text-transform: none;
      letter-spacing: normal;
      padding: 6px 12px;
      background-color: $light-primary !important; // #EB575D
      color: $light-surface-2 !important; // #FFFFFF
    }
  }
}
</style>
